/**
 * Paleta wyszukiwania (⌘K / Ctrl+K) — jedno pole, trzy sekcje wyników.
 *
 * Sekcyjność, a nie jedna lista wg rankingu, jest decyzją produktową przeniesioną z
 * `PLAN_WYSZUKIWARKA.md` §2: kto szuka słowa, oczekuje najpierw tego słowa, a wynik
 * semantyczny bez szukanego słowa wygląda na pomyłkę, dopóki nie jest jawnie
 * oddzielony kreską „Wyniki bliskoznaczne".
 *
 * Snippety wstawiamy przez `dangerouslySetInnerHTML`: backend escapuje CAŁY tekst i
 * przepuszcza wyłącznie `<mark>` (`app/modules/search/fts.py`). Nic innego z tej
 * odpowiedzi nie trafia do DOM jako HTML.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EmptyState, StatusChip } from '@/design/components'
import { cn } from '@/lib/cn'
import { MIN_QUERY_LENGTH, useDebounced, useSearch, useSemanticFollowUp } from './hooks'
import type { CaseHit, DocumentHit } from './types'

export interface SearchPaletteProps {
  open: boolean
  onClose: () => void
}

/** Element listy sprowadzony do wspólnego mianownika — po nim chodzi klawiatura. */
interface Row {
  key: string
  href: string
}

/** Statusy, które warto zaznaczyć przy trafieniu (pismo jeszcze nieprzejrzane). */
const STATUS_NOTE: Record<string, { label: string; variant: 'warning' | 'danger' }> = {
  pending_verification: { label: 'Do weryfikacji', variant: 'warning' },
  low_confidence: { label: 'Niska pewność', variant: 'danger' },
}

function documentHref(hit: DocumentHit): string {
  // Weryfikacja pisma to jedyny widok pokazujący treść z podglądem strony.
  const page = hit.page_no ? `?page=${hit.page_no}` : ''
  return `/dokumenty/weryfikacja/${hit.document_id}${page}`
}

export function SearchPalette({ open, onClose }: SearchPaletteProps) {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const debounced = useDebounced(query)

  const search = useSearch(debounced, open)
  const data = search.data
  const followUp = useSemanticFollowUp(debounced, Boolean(open && data?.semantic_pending))

  const cases = data?.cases ?? []
  const documents = data?.documents ?? []
  // Sekcja bliskoznaczna pochodzi z pierwszej odpowiedzi ALBO z dogrywki — nigdy z obu
  // naraz (`semantic_pending` znaczy, że pierwsza jej nie miała).
  const semantic = data?.semantic?.length ? data.semantic : (followUp.data?.semantic ?? [])

  const rows: Row[] = useMemo(
    () => [
      ...cases.map((c) => ({ key: `case-${c.id}`, href: `/sprawy/${c.id}` })),
      ...documents.map((d) => ({ key: `doc-${d.chunk_id}`, href: documentHref(d) })),
      ...semantic.map((d) => ({ key: `sem-${d.chunk_id}`, href: documentHref(d) })),
    ],
    [cases, documents, semantic],
  )

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  useEffect(() => {
    setActive(0)
  }, [debounced])

  const go = useCallback(
    (href: string) => {
      onClose()
      setQuery('')
      navigate(href)
    },
    [navigate, onClose],
  )

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }
    if (rows.length === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((i) => (i + 1) % rows.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((i) => (i - 1 + rows.length) % rows.length)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const row = rows[active]
      if (row) go(row.href)
    }
  }

  if (!open) return null

  const tooShort = debounced.trim().length < MIN_QUERY_LENGTH
  const nothing = !tooShort && !search.isPending && rows.length === 0
  let index = -1

  return (
    <div className="fixed inset-0 z-50 flex justify-center px-4 pt-[12vh]" role="presentation">
      {/* Kliknięcie w tło zamyka — jak w modalu bazowym. */}
      <button
        type="button"
        aria-label="Zamknij wyszukiwanie"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-[rgba(15,43,61,0.32)]"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Wyszukiwanie"
        className="relative flex max-h-[70vh] w-full max-w-[720px] flex-col overflow-hidden rounded-md border border-line bg-card shadow-lg"
      >
        <div className="flex items-center gap-2.5 border-b border-line-soft px-4">
          <svg viewBox="0 0 14 14" aria-hidden className="h-4 w-4 shrink-0 text-muted">
            <circle cx="6" cy="6" r="4.3" fill="none" stroke="currentColor" strokeWidth="1.2" />
            <path d="M9.4 9.4 13 13" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Szukaj spraw, dokumentów, klienta…"
            aria-label="Szukaj"
            className="h-[52px] w-full bg-transparent text-base text-ink outline-none placeholder:text-muted"
          />
          {search.isFetching ? <Spinner /> : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto py-1.5">
          {tooShort ? (
            <p className="px-4 py-6 text-center text-small text-muted">
              Wpisz co najmniej {MIN_QUERY_LENGTH} znaki — sygnaturę, nazwisko klienta albo
              słowo z treści pisma.
            </p>
          ) : null}

          {search.isError ? (
            <p className="px-4 py-6 text-center text-small text-danger">
              Nie udało się wyszukać. Spróbuj ponownie.
            </p>
          ) : null}

          {nothing && !search.isError ? (
            <EmptyState
              title="Brak wyników"
              description={`Dla frazy „${debounced.trim()}” nie znaleziono ani sprawy, ani pisma w Twoim zakresie.`}
            />
          ) : null}

          {cases.length > 0 ? (
            <Section title="Sprawy">
              {cases.map((hit) => {
                index += 1
                return (
                  <CaseRow
                    key={hit.id}
                    hit={hit}
                    active={index === active}
                    onSelect={() => go(`/sprawy/${hit.id}`)}
                  />
                )
              })}
            </Section>
          ) : null}

          {documents.length > 0 ? (
            <Section title="Dokumenty">
              {documents.map((hit) => {
                index += 1
                return (
                  <DocumentRow
                    key={hit.chunk_id}
                    hit={hit}
                    active={index === active}
                    onSelect={() => go(documentHref(hit))}
                  />
                )
              })}
            </Section>
          ) : null}

          {semantic.length > 0 ? (
            <Section
              title="Wyniki bliskoznaczne"
              hint="Pisma o zbliżonym znaczeniu — bez dosłownego trafienia we frazę."
              divider
            >
              {semantic.map((hit) => {
                index += 1
                return (
                  <DocumentRow
                    key={hit.chunk_id}
                    hit={hit}
                    active={index === active}
                    onSelect={() => go(documentHref(hit))}
                  />
                )
              })}
            </Section>
          ) : null}

          {followUp.isFetching ? (
            <p className="px-4 py-3 text-center text-small text-muted">
              Szukam wyników bliskoznacznych…
            </p>
          ) : null}
        </div>

        <div className="flex items-center gap-3 border-t border-line-soft px-4 py-2 text-[11.5px] text-muted">
          <span>
            <kbd className="rounded-[2px] border border-line-soft px-1 font-mono">↑↓</kbd> wybór
          </span>
          <span>
            <kbd className="rounded-[2px] border border-line-soft px-1 font-mono">↵</kbd> otwórz
          </span>
          <span>
            <kbd className="rounded-[2px] border border-line-soft px-1 font-mono">esc</kbd> zamknij
          </span>
        </div>
      </div>
    </div>
  )
}

function Section({
  title,
  hint,
  divider,
  children,
}: {
  title: string
  hint?: string
  divider?: boolean
  children: React.ReactNode
}) {
  return (
    <section className={cn(divider && 'mt-1.5 border-t border-line-soft pt-1.5')}>
      <header className="flex items-baseline gap-2 px-4 pb-1 pt-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">{title}</h2>
        {hint ? <span className="truncate text-[11px] text-muted opacity-80">{hint}</span> : null}
      </header>
      {children}
    </section>
  )
}

const ROW_CLASS =
  'block w-full cursor-pointer px-4 py-2 text-left transition-colors duration-fast hover:bg-[#eef1f4]'

function CaseRow({
  hit,
  active,
  onSelect,
}: {
  hit: CaseHit
  active: boolean
  onSelect: () => void
}) {
  const signature = hit.court_signature ?? hit.internal_no ?? '—'
  return (
    <button type="button" onClick={onSelect} className={cn(ROW_CLASS, active && 'bg-[#eef1f4]')}>
      <span className="flex items-baseline gap-2">
        <span className="font-mono text-small text-navy">{signature}</span>
        <span className="truncate text-base text-ink">{hit.title}</span>
      </span>
      {hit.client_name ? (
        <span className="mt-0.5 block truncate text-small text-muted">{hit.client_name}</span>
      ) : null}
    </button>
  )
}

function DocumentRow({
  hit,
  active,
  onSelect,
}: {
  hit: DocumentHit
  active: boolean
  onSelect: () => void
}) {
  const note = STATUS_NOTE[hit.status]
  return (
    <button type="button" onClick={onSelect} className={cn(ROW_CLASS, active && 'bg-[#eef1f4]')}>
      <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="truncate text-base text-ink">{hit.title}</span>
        {hit.case_signature ? (
          <span className="font-mono text-small text-muted">{hit.case_signature}</span>
        ) : (
          <span className="text-small text-muted">skrzynka wpływu</span>
        )}
        {hit.page_no ? <span className="text-small text-muted">s. {hit.page_no}</span> : null}
        {note ? (
          <StatusChip variant={note.variant} size="sm">
            {note.label}
          </StatusChip>
        ) : null}
      </span>
      {/* Bezpieczne: backend escapuje treść i przepuszcza wyłącznie <mark>. */}
      <span
        className="mt-0.5 block text-small leading-[1.45] text-muted [&_mark]:bg-[#fdf0c8] [&_mark]:text-ink"
        dangerouslySetInnerHTML={{ __html: hit.snippet }}
      />
    </button>
  )
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-[1.5px] border-line border-t-navy"
    />
  )
}
